"use client";

import { useState, useEffect, useRef, memo } from "react";
import { Star, StarHalf, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { EscuelaCardProps } from "./types";

// Función para renderizar estrellas de calificación
const renderStars = (calificaciones: number, totalCalificaciones: number) => {
  const stars = [];
  const rating =
    totalCalificaciones > 0 ? calificaciones / totalCalificaciones : 0;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <Star
        key={`full-${i}`}
        color="#ffa600"
        className="w-4 h-4 xl:w-5 xl:h-5 fill-[#e9be00] text-primary"
      />
    );
  }

  if (hasHalfStar) {
    stars.push(
      <StarHalf
        key="half"
        color="#ffa600"
        className="w-4 h-4 xl:w-5 xl:h-5 fill-[#e9be00] text-primary"
      />
    );
  }

  const emptyStars = 5 - stars.length;
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <Star key={`empty-${i}`} className="w-4 h-4 xl:w-5 xl:h-5 text-muted-foreground" />
    );
  }

  return stars;
};

// Componente de tarjeta memoizado para evitar re-renderizados innecesarios
export const EscuelaCard = memo(({
  item,
  index,
  isEscuela,
  selectedType,
}: EscuelaCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const router = useRouter();

  useEffect(() => {
    if (imageRef.current?.complete) {
      setImageLoaded(true);
      setTimeout(() => setFadeIn(true), 50);
    }
  }, []);

  const handleCardClick = () => {
    router.push(`/clases/${item.usuarioId}/${selectedType}`);
  };

  return (
    <Card
      key={`card-${item.usuarioId || index}`}
      className="max-w-sm bg-[#fdfdfd] w-full rounded-2xl border-[#dee1e7] border-2 mx-auto overflow-hidden h-[420px] md:h-[440px] xl:h-[490px] shadow-2xl z-[9999] relative hover:shadow-4xl hover:border-[#7090d1] transition-all duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="relative h-48 md:h-[200px] xl:h-64 overflow-hidden p-[.4rem] rounded-xl">
        <div
          className={`absolute inset-0 w-full h-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-xl z-10 ${fadeIn ? 'fade-out' : 'fade-in'}`}
          style={{ backgroundSize: '200% 100%' }}
        ></div>

        <img
          ref={imageRef}
          src={item.profilePic || "/surferDefault.png"}
          alt={`${isEscuela ? "Escuela" : "Instructor"} ${item.nombre}`}
          className={`object-cover w-full  h-full rounded-xl z-5 ${fadeIn ? 'fade-in' : 'opacity-0'}`}
          loading={index < 2 ? "eager" : "lazy"}
          onLoad={() => {
            setImageLoaded(true);
            setTimeout(() => setFadeIn(true), 50);
          }}
          onError={() => {
            if (imageRef.current) {
              imageRef.current.src = "/surferDefault.png";
            }
            setImageLoaded(true);
            setTimeout(() => setFadeIn(true), 50);
          }}
        />

        <div className="flex items-center space-x-1 absolute top-3 shadow-lg right-3 bg-[#ffffffbd] backdrop-blur-[3px] rounded-xl p-2 z-20">
          {renderStars(item.calificaciones, item.totalCalificaciones)}
          <span className="text-gray-800 text-[0.85rem] md:text-[1rem] pl-2 md:pl-3">
            ({item?.totalCalificaciones})
          </span>
        </div>
      </div>

      <CardHeader className="py-2 md:py-3 gap-0 flex flex-row justify-between items-center relative">
        <div className="flex flex-col gap-1 md:gap-2 w-full relative">
          <div className="flex items-center gap-2">
            <h2 className="text-xl md:text-2xl font-bold">{item.nombre}</h2>
            {item.tieneInstitucion && (
              <BadgeCheck className="w-4 h-4 md:w-5 md:h-5 text-blue-500 relative -top-1" />
            )}
          </div>
          <p className="text-muted-foreground line-clamp-2 text-xs md:text-sm min-h-[2rem] md:min-h-[2.2rem] w-full">
            {item.nombreInstitucion}
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs md:text-sm text-muted-foreground">Dirección:</span>
          <span className="text-[#222222] text-xs md:text-sm">{item.direccion}</span>
        </div>

        <hr className="border-t border-[#3088ec1e]" />
      </CardContent>

      <CardFooter className="flex justify-center items-center w-full absolute bottom-0">
        <Link
          href={`/clases/${item.usuarioId}/${selectedType}`}
          className="w-full"
          onClick={(e) => e.stopPropagation()} // Evitar doble navegación
        >
          <Button
            className="w-full !bg-gradient-to-tr !from-blue-500 !via-blue-500 !to-blue-300"
            size="lg"
          >
            Ver Perfil
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
});

EscuelaCard.displayName = 'EscuelaCard'; 